<script setup>
// Shared failure-recovery panel for the check-in validation set.
// Amber (fixable-not-fatal) status icon + serif headline + plain-language
// recovery steps + Try again / Use venue PIN / Chat footer. Used by GpsBody
// (denied / unavailable / timeout / outside) and QrTakeover (camera-denied).
//
// All colors except the amber badge and the primary CTA ride on currentColor
// so the panel renders correctly on the light sheet AND the dark QR takeover.
//
// `steps` accepts either:
//   • { ios: string[], android: string[] } — OS-aware instructions with a
//     "Using Android?/Using iPhone?" toggle link, or
//   • string[] — OS-agnostic numbered steps (no toggle), or
//   • null — no steps (body-only states like timeout).
import { ref, computed } from 'vue'

const props = defineProps({
  // Lucide icon component rendered inside the amber badge.
  icon: { type: [Object, Function], required: true },
  headline: { type: String, required: true },
  // Optional paragraph shown under the headline (before any steps).
  body: { type: String, default: null },
  steps: { type: [Object, Array], default: null },
  // Shows the outlined "Use venue PIN instead" secondary CTA.
  pinAvailable: { type: Boolean, default: false },
  // Unique prefix for the GTM ids of this panel's controls.
  idPrefix: { type: String, default: 'checkin-recovery' },
  // True on non-sheet surfaces (QR takeover): drops the sticky footer's
  // surface background / negative margins so the panel sits flush on black.
  bare: { type: Boolean, default: false },
})

defineEmits(['retry', 'use-pin', 'help'])

// Detect the OS once — the toggle link lets users flip if detection is wrong
// (e.g. iPadOS reporting a desktop UA).
const detectedOs = /iPhone|iPad|iPod/i.test(
  typeof navigator !== 'undefined' ? navigator.userAgent : '',
)
  ? 'ios'
  : 'android'

const os = ref(detectedOs)

const osAware = computed(() => Boolean(props.steps) && !Array.isArray(props.steps))

const visibleSteps = computed(() => {
  if (!props.steps) return []
  if (Array.isArray(props.steps)) return props.steps
  return props.steps[os.value] ?? []
})

const toggleLabel = computed(() => (os.value === 'ios' ? 'Using Android?' : 'Using iPhone?'))

const flipOs = () => {
  os.value = os.value === 'ios' ? 'android' : 'ios'
}
</script>

<template>
  <div class="cs-recovery" :class="{ 'cs-recovery--bare': bare }">
    <div class="cs-recovery__badge">
      <component :is="icon" :size="20" />
    </div>

    <div class="cs-recovery__headline">{{ headline }}</div>

    <p v-if="body" class="cs-recovery__body">{{ body }}</p>

    <ol v-if="visibleSteps.length" class="cs-recovery__steps">
      <li v-for="(step, i) in visibleSteps" :key="`${os}-${i}`" class="cs-recovery__step">
        <span v-if="visibleSteps.length > 1" class="cs-recovery__step-num">{{ i + 1 }}</span>
        <span class="cs-recovery__step-text">{{ step }}</span>
      </li>
    </ol>

    <div v-if="osAware" class="cs-recovery__os">
      <button
        :id="`${idPrefix}-os-toggle`"
        type="button"
        class="cs-recovery__os-toggle gtm-click"
        @click="flipOs"
      >
        {{ toggleLabel }}
      </button>
    </div>

    <!-- No self-serve fallback (no PIN/QR): don't dead-end on Try again —
         promote support chat to a full secondary button. We deliberately do NOT
         point to venue staff: these are small local businesses whose staff
         aren't trained on the giveaway and can't help, which only frustrates. -->
    <div class="cs-recovery__actions">
      <button
        :id="`${idPrefix}-retry`"
        type="button"
        class="cs-recovery__cta gtm-click"
        @click="$emit('retry')"
      >
        Try again
      </button>
      <button
        v-if="pinAvailable"
        :id="`${idPrefix}-use-pin`"
        type="button"
        class="cs-recovery__cta-outline gtm-click"
        @click="$emit('use-pin')"
      >
        Use venue PIN instead
      </button>
      <button
        v-else
        :id="`${idPrefix}-help-cta`"
        type="button"
        class="cs-recovery__cta-outline gtm-click"
        @click="$emit('help')"
      >
        Chat with us
      </button>
      <div v-if="pinAvailable" class="cs-recovery__help">
        <button
          :id="`${idPrefix}-help`"
          type="button"
          class="cs-recovery__help-link gtm-click"
          @click="$emit('help')"
        >
          Still stuck? Chat with us
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

.cs-recovery {
  padding-top: 4px; // pt-1 — matches the GPS body rhythm
  text-align: center;

  > * + * {
    margin-top: 12px; // space-y-3
  }

  &__badge {
    @include cs-recovery-badge;
  }

  &__headline {
    font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
  }

  // `margin-top` (not `margin: 0`) so it doesn't defeat the `> * + *` spacer at
  // equal specificity + later source order (which left it hugging the headline).
  &__body {
    margin: 16px 0 0;
    font-size: 13px;
    line-height: 1.45;
    color: cs-current(70%);
  }

  &__steps {
    margin: 16px 0 0;
    padding: 0;
    list-style: none;
    text-align: left;

    > * + * {
      margin-top: 8px; // space-y-2
    }
  }

  &__step {
    display: flex;
    align-items: center; // vertically center the number against the step text
    gap: 8px; // gap-2
    font-size: 13px;
    line-height: 1.45;
  }

  // Same 20px mono circle as the quiz choice letters.
  &__step-num {
    display: inline-grid;
    height: 20px;
    width: 20px;
    flex-shrink: 0;
    place-items: center;
    border-radius: 9999px;
    background: cs-current(10%);
    font-family: $cs-font-mono;
    font-size: 10px;
    font-weight: 700;
  }

  &__step-text {
    min-width: 0;
    color: cs-current(80%);
  }

  &__os {
    text-align: center;
  }

  &__os-toggle {
    @include cs-text-link;
    min-height: 44px;
    font-size: 11px;
  }

  &__actions {
    @include cs-sticky-footer;

    button {
      margin-top: 8px;
    }
  }

  &--bare &__actions {
    position: static;
    margin: 0;
    padding: 8px 0;
    background: transparent;
  }

  &__cta {
    @include cs-cta;
    min-height: 44px;
  }

  &__cta-outline {
    @include cs-cta-outline;
  }

  &__help {
    text-align: center;
  }

  &__help-link {
    @include cs-text-link;
  }
}
</style>
